import { Component, Input } from '@angular/core';
import { UserResponse, OrderResponse } from '../../../../@shared/models/response.module';
import { PaymentService } from '../../../../@shared/services/payment.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-user-profile-orders',
  standalone: false,
  templateUrl: './user-profile-orders.html',
  styleUrl: './user-profile-orders.css'
})
export class UserProfileOrders {
  @Input() user: UserResponse;

  allOrders: OrderResponse[] = [];
  pendingOrders: OrderResponse[] = [];
  paidOrders: OrderResponse[] = [];
  cancelledOrders: OrderResponse[] = [];

  loading: boolean = true;
  payingOrderId: number | null = null;
  cancellingOrderId: number | null = null;
  expandedOrderId: number | null = null;

  activeFilter: 'all' | 'pending' | 'paid' | 'cancelled' = 'all';

  constructor(
    private paymentService: PaymentService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    if (this.user?.user_id) {
      this.loadOrders();
    }
  }

  ngOnChanges() {
    if (this.user?.user_id) {
      this.loadOrders();
    }
  }

  loadOrders() {
    this.loading = true;
    this.paymentService.getOrders({ user_id: this.user.user_id }).subscribe({
      next: (orders) => {
        this.allOrders = orders.sort((a, b) => {
          const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
          const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
          return dateB - dateA; // Most recent first
        });

        this.pendingOrders = this.allOrders.filter(o => o.status === 'pending');
        this.paidOrders = this.allOrders.filter(o => o.status === 'paid');
        this.cancelledOrders = this.allOrders.filter(o => o.status === 'cancelled');

        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  get filteredOrders(): OrderResponse[] {
    switch (this.activeFilter) {
      case 'pending':
        return this.pendingOrders;
      case 'paid':
        return this.paidOrders;
      case 'cancelled':
        return this.cancelledOrders;
      default:
        return this.allOrders;
    }
  }

  toggleOrderDetails(order: OrderResponse) {
    if (this.expandedOrderId === order.order_id) {
      this.expandedOrderId = null;
    } else {
      this.expandedOrderId = order.order_id;
    }
  }

  payOrder(order: OrderResponse) {
    this.payingOrderId = order.order_id;
    this.paymentService.payPendingOrder(order.order_id).subscribe({
      next: (response) => {
        window.location.href = response.checkout_url;
      },
      error: (err) => {
        console.error('Failed to create checkout session:', err);
        this.payingOrderId = null;
      }
    });
  }

  cancelOrder(order: OrderResponse) {
    if (!confirm(this.translateService.instant('orders.cancelConfirm'))) {
      return;
    }

    this.cancellingOrderId = order.order_id;
    this.paymentService.cancelOrder(order.order_id).subscribe({
      next: () => {
        this.loadOrders();
        this.cancellingOrderId = null;
      },
      error: (err) => {
        console.error('Failed to cancel order:', err);
        this.cancellingOrderId = null;
      }
    });
  }

  getStatusTranslation(status: string): string {
    switch (status) {
      case 'pending':
        return this.translateService.instant('orders.pending');
      case 'paid':
        return this.translateService.instant('orders.paid');
      case 'cancelled':
        return this.translateService.instant('orders.cancelled');
      default:
        return status;
    }
  }

  downloadReceipt(order: OrderResponse) {
    // Generate a simple receipt
    const receiptContent = this.generateReceiptContent(order);
    const blob = new Blob([receiptContent], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `receipt-order-${order.order_id}.html`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private generateReceiptContent(order: OrderResponse): string {
    // Get company info (seller information)
    const company = order.company || order.event?.company;
    const companyName = company?.company_name || 'N/A';
    const orgNumber = company?.organisation_number || 'N/A';
    const companyAddress = company ? `${company.address || ''}, ${company.postal_code || ''} ${company.city || ''}`.trim() : '';
    const companyContact = company?.email || '';

    // Format purchase date
    const purchaseDate = this.formatDateNorwegian(order.paid_at || order.created_at);

    // Generate order lines table with VAT info
    const lines = order.order_lines?.map(line => {
      const vatRateDisplay = line.vat_rate ? `${line.vat_rate}%` : '0%';
      const vatCentsDisplay = line.vat_cents || 0;
      return `
      <tr>
        <td style="padding: 10px 8px; border-bottom: 1px solid #e0e0e0;">${line.description}</td>
        <td style="padding: 10px 8px; border-bottom: 1px solid #e0e0e0; text-align: center;">${line.quantity}</td>
        <td style="padding: 10px 8px; border-bottom: 1px solid #e0e0e0; text-align: right;">${this.formatCurrency(line.unit_price_cents, order.currency)}</td>
        <td style="padding: 10px 8px; border-bottom: 1px solid #e0e0e0; text-align: center;">${vatRateDisplay}</td>
        <td style="padding: 10px 8px; border-bottom: 1px solid #e0e0e0; text-align: right;">${this.formatCurrency(vatCentsDisplay, order.currency)}</td>
        <td style="padding: 10px 8px; border-bottom: 1px solid #e0e0e0; text-align: right;">${this.formatCurrency(line.total_cents, order.currency)}</td>
      </tr>
    `}).join('') || '';

    // Calculate amounts
    const subtotalCents = order.subtotal_cents || order.total_cents || 0;
    const totalCents = order.total_cents || 0;
    const totalVatCents = order.total_vat_cents || 0;
    const subtotalExVat = totalCents - totalVatCents;

    // Group VAT by rate for breakdown
    const vatByRate: { [key: string]: number } = {};
    order.order_lines?.forEach(line => {
      const rate = (line.vat_rate || 0).toString();
      vatByRate[rate] = (vatByRate[rate] || 0) + (line.vat_cents || 0);
    });

    // Generate VAT breakdown rows
    const vatBreakdownRows = Object.entries(vatByRate)
      .filter(([rate, cents]) => cents > 0)
      .map(([rate, cents]) => `
      <div class="total-row">
        <span>MVA ${rate}%</span>
        <span>${this.formatCurrency(cents, order.currency)}</span>
      </div>
    `).join('');

    return `
<!DOCTYPE html>
<html lang="no">
<head>
  <meta charset="UTF-8">
  <title>Kvittering - Ordre #${order.order_id}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; max-width: 700px; margin: 0 auto; padding: 40px 20px; color: #333; line-height: 1.5; }
    .receipt { background: #fff; border: 1px solid #ddd; border-radius: 8px; overflow: hidden; }
    .header { background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%); color: #fff; padding: 30px; text-align: center; }
    .header h1 { margin: 0 0 5px 0; font-size: 28px; font-weight: 600; }
    .header .receipt-number { font-size: 14px; opacity: 0.9; }
    .section { padding: 20px 30px; border-bottom: 1px solid #eee; }
    .section:last-child { border-bottom: none; }
    .section-title { font-size: 12px; text-transform: uppercase; color: #888; margin-bottom: 10px; font-weight: 600; letter-spacing: 0.5px; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
    .info-block { }
    .info-label { font-size: 12px; color: #888; margin-bottom: 2px; }
    .info-value { font-size: 14px; font-weight: 500; }
    .seller-info { background: #f8f9fa; }
    table { width: 100%; border-collapse: collapse; margin: 0; }
    th { background: #f5f5f5; padding: 12px 8px; text-align: left; font-size: 12px; text-transform: uppercase; color: #666; font-weight: 600; }
    .totals { background: #f8f9fa; }
    .total-row { display: flex; justify-content: space-between; padding: 8px 0; font-size: 14px; }
    .total-row.grand-total { font-size: 18px; font-weight: 700; padding-top: 12px; margin-top: 8px; border-top: 2px solid #333; }
    .payment-info { display: flex; justify-content: space-between; align-items: center; }
    .payment-method { display: flex; align-items: center; gap: 8px; }
    .payment-badge { background: #e8f5e9; color: #2e7d32; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
    .footer { text-align: center; padding: 20px 30px; background: #fafafa; font-size: 12px; color: #888; }
    .footer p { margin: 5px 0; }
    @media print { body { margin: 0; padding: 20px; } .receipt { border: none; } }
  </style>
</head>
<body>
  <div class="receipt">
    <!-- Header -->
    <div class="header">
      <h1>KVITTERING</h1>
      <div class="receipt-number">Kvitteringsnummer: #${order.order_id}</div>
    </div>

    <!-- Seller Information -->
    <div class="section seller-info">
      <div class="section-title">Selger</div>
      <div class="info-grid">
        <div class="info-block">
          <div class="info-label">Firmanavn</div>
          <div class="info-value">${companyName}</div>
        </div>
        <div class="info-block">
          <div class="info-label">Organisasjonsnummer</div>
          <div class="info-value">${orgNumber}</div>
        </div>
        ${companyAddress ? `
        <div class="info-block">
          <div class="info-label">Adresse</div>
          <div class="info-value">${companyAddress}</div>
        </div>` : ''}
        ${companyContact ? `
        <div class="info-block">
          <div class="info-label">E-post</div>
          <div class="info-value">${companyContact}</div>
        </div>` : ''}
      </div>
    </div>

    <!-- Purchase Information -->
    <div class="section">
      <div class="section-title">Kjøpsinformasjon</div>
      <div class="info-grid">
        <div class="info-block">
          <div class="info-label">Kjøpsdato</div>
          <div class="info-value">${purchaseDate}</div>
        </div>
        <div class="info-block">
          <div class="info-label">Betalingsstatus</div>
          <div class="info-value">${this.getStatusNorwegian(order.status)}</div>
        </div>
        ${order.event ? `
        <div class="info-block">
          <div class="info-label">Arrangement</div>
          <div class="info-value">${order.event.event_name}</div>
        </div>` : ''}
        ${order.paid_at ? `
        <div class="info-block">
          <div class="info-label">Betalingsdato</div>
          <div class="info-value">${this.formatDateNorwegian(order.paid_at)}</div>
        </div>` : ''}
      </div>
    </div>

    <!-- Order Lines (Description) -->
    <div class="section">
      <div class="section-title">Beskrivelse</div>
      <table>
        <thead>
          <tr>
            <th>Vare/Tjeneste</th>
            <th style="text-align: center;">Antall</th>
            <th style="text-align: right;">Enhetspris</th>
            <th style="text-align: center;">MVA %</th>
            <th style="text-align: right;">MVA</th>
            <th style="text-align: right;">Beløp</th>
          </tr>
        </thead>
        <tbody>
          ${lines}
        </tbody>
      </table>
    </div>

    <!-- Totals (Amount) -->
    <div class="section totals">
      <div class="section-title">Beløp</div>
      <div class="total-row">
        <span>Sum eks. MVA</span>
        <span>${this.formatCurrency(subtotalExVat, order.currency)}</span>
      </div>
      ${vatBreakdownRows || `
      <div class="total-row">
        <span>MVA 0%</span>
        <span>${this.formatCurrency(0, order.currency)}</span>
      </div>`}
      <div class="total-row" style="border-top: 1px solid #ddd; padding-top: 8px; margin-top: 4px;">
        <span>Total MVA</span>
        <span>${this.formatCurrency(totalVatCents, order.currency)}</span>
      </div>
      <div class="total-row grand-total">
        <span>Totalt inkl. MVA</span>
        <span>${this.formatCurrency(totalCents, order.currency)}</span>
      </div>
    </div>

    <!-- Payment Method -->
    <div class="section">
      <div class="section-title">Betalingsmåte</div>
      <div class="payment-info">
        <div class="payment-method">
          <span>Kort (Stripe)</span>
          ${order.status === 'paid' ? '<span class="payment-badge">Betalt</span>' : ''}
        </div>
        ${order.stripe_payment_intent_id ? `<span style="font-size: 12px; color: #888;">Ref: ${order.stripe_payment_intent_id.slice(-8)}</span>` : ''}
      </div>
    </div>

    <!-- Footer -->
    <div class="footer">
      <p>Takk for handelen!</p>
      <p>Kvittering generert: ${new Date().toLocaleDateString('nb-NO', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
    </div>
  </div>
</body>
</html>`;
  }

  private formatDateNorwegian(dateInput: string | Date | null | undefined): string {
    if (!dateInput) return 'N/A';
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    return date.toLocaleDateString('nb-NO', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  private getStatusNorwegian(status: string): string {
    switch (status) {
      case 'paid': return 'Betalt';
      case 'pending': return 'Venter på betaling';
      case 'cancelled': return 'Kansellert';
      case 'refunded': return 'Refundert';
      case 'failed': return 'Mislykket';
      default: return status;
    }
  }

  formatCurrency(cents: number, currency: string = 'NOK'): string {
    return (cents / 100).toFixed(2) + ' ' + currency;
  }

  formatDate(dateInput: string | Date | null | undefined): string {
    if (!dateInput) return '';
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'paid':
        return 'badge-light-success';
      case 'pending':
        return 'badge-light-warning';
      case 'cancelled':
        return 'badge-light-danger';
      default:
        return 'badge-light-secondary';
    }
  }

  getSubtotalExVat(order: OrderResponse): number {
    const totalVat = order.total_vat_cents || 0;
    return (order.subtotal_cents || order.total_cents || 0) - totalVat;
  }
}
